import {CONTEST_ACTIONS} from './contestActions.js';
import {APP_STATUS} from './appStatus.js';
import {APP_STEPS} from './contestSteps.js';
import {getSmartView} from '@/utils/leaderboardEngine.js';
import {upsertParticipant} from '@/utils/participantHelpers.js';
import {computeRugbyLeaderboard} from "@/utils/rugbyLeaderboardEngine.js";
import {computeFootballLeaderboard} from "@/utils/footballLeaderboardEngine.js";

export const initialState = {
    status: APP_STATUS.FETCHING,
    error: null,
    isLoading: false,
    step: APP_STEPS.GAME,

    contestData: null,
    matchScore: {team1: 0, team2: 0},
    userScore: {team1: 0, team2: 0},
    userInfo: {pseudo: '', email: '', optIn: false, quizPoints: null},
    isAnonymous: true,
    // Referral: resolution of the ?ref= code ({valid, reason, referrerPseudo, ...}); referralApplied is set
    // from the sign-up response so the confirmation can honestly promise the reward email.
    referral: null,
    referralApplied: false,
    canPlayQuiz: false,
    hasZone: false,
    halfTimeReward: null,
    penaltyReward: null,
    // Live penalty step from /me (null unless the shootout is on/just finished).
    penaltyState: null,
    // Dedicated shootout leaderboard rows (from /penalty-leaderboard): {rank, pseudo, isMe, score, kicks}.
    penaltyBoard: [],
    userRugbyBet: {
        selection: null,
        margin: 0,
        winningTeamId: null
    },

    leaderboard: [],
    leaderboardFull: [],
    userRankData: {
        rank: 0,
        points: 0,
        highlight: false,
        trend: 'neutral',
        diff: 0,
        reward: null
    },
    calculations: {total: 0, win: 0, score: 0, oddsWin: 0, oddsScore: 0},

    // --- OVERLAYS ---
    overlays: {
        goal: false,
        qr: {isOpen: false, url: '', title: ''},
        selectedPlayer: null
    }
};

// ==========================================
// 2. HELPER
// ==========================================
const recalculateState = (allParticipants, matchScore, currentRankData, contestData) => {
    if (!contestData && !contestData?.type)
        return;

    let rankedList;

    if (contestData?.type === 'RUGBY') {
        rankedList = computeRugbyLeaderboard(
            allParticipants,
            matchScore.team1,
            matchScore.team2,
            contestData.fixture
        );
    } else if (contestData?.type === 'FOOTBALL') {
        rankedList = computeFootballLeaderboard(
            allParticipants,
            matchScore.team1,
            matchScore.team2
        );
    }
    const displayList = getSmartView(rankedList);

    let newRankData = currentRankData;
    const me = rankedList.find(p => p.isMe);

    if (me) {
        const prevRank = currentRankData.rank || me.rank;
        const diff = prevRank - me.rank;
        const trend = diff > 0 ? 'up' : (diff < 0 ? 'down' : 'neutral');

        newRankData = {
            ...currentRankData,
            rank: me.rank,
            points: me.points,
            highlight: true,
            trend,
            diff: Math.abs(diff)
        };
    }

    return {fullList: rankedList, displayList, newRankData};
};

// ==========================================
// 3. REDUCER (Le Cerveau)
// ==========================================
export function contestReducer(state, action) {
    switch (action.type) {

        // --- GESTION DE STATUT ---
        case CONTEST_ACTIONS.SET_FETCHING:
            return {...state, status: action.payload ? APP_STATUS.FETCHING : state.status};

        case CONTEST_ACTIONS.SET_ERROR:
            return {...state, status: APP_STATUS.ERROR, error: action.payload, isLoading: false};

        case CONTEST_ACTIONS.SET_FORM_ERROR:
            return {...state, error: action.payload, isLoading: false};

        case CONTEST_ACTIONS.INIT_SUCCESS:
            return {
                ...state,
                status: APP_STATUS.READY,
                error: null,
                contestData: action.payload.contest,
                matchScore: action.payload.matchScore,
                userScore: action.payload.matchScore,
            };

        // --- UI & NAVIGATION ---
        case CONTEST_ACTIONS.SET_LOADING:
            return {...state, isLoading: action.payload};
        case CONTEST_ACTIONS.SET_STEP:
            return {...state, error: null, step: action.payload};

        // --- USER INFO ---
        case CONTEST_ACTIONS.UPDATE_USER_INFOS:
            return {
                ...state,
                userInfo: {...state.userInfo, ...(action.payload.info ?? {})},
                userScore: action.payload.score ? {...state.userScore, ...action.payload.score} : state.userScore,
                isAnonymous: action.payload.isAnonymous ?? state.isAnonymous,
                canPlayQuiz: action.payload.canPlayQuiz ?? state.canPlayQuiz,
                hasZone: action.payload.hasZone ?? state.hasZone
            };

        case CONTEST_ACTIONS.UPDATE_RUGBY_BET:
            return {
                ...state,
                userRugbyBet: {...state.userRugbyBet, ...action.payload}
            };

        case CONTEST_ACTIONS.SET_USER_REWARD:
            return {
                ...state,
                userRankData: {...state.userRankData, reward: action.payload}
            };

        case CONTEST_ACTIONS.SET_REFERRAL:
            return {...state, referral: action.payload};

        case CONTEST_ACTIONS.SET_REFERRAL_APPLIED:
            return {...state, referralApplied: action.payload};

        case CONTEST_ACTIONS.SET_HALF_TIME_REWARD:
            return {...state, halfTimeReward: action.payload};

        case CONTEST_ACTIONS.SET_PENALTY_REWARD:
            return {...state, penaltyReward: action.payload};

        case CONTEST_ACTIONS.SET_PENALTY_STATE:
            return {...state, penaltyState: action.payload};

        case CONTEST_ACTIONS.SET_PENALTY_BOARD:
            return {...state, penaltyBoard: action.payload};

        case CONTEST_ACTIONS.UPDATE_CALCULATIONS:
            return {...state, calculations: action.payload};

        // --- LOGIQUE LIVE & CLASSEMENT (La partie "Intelligente") ---

        case CONTEST_ACTIONS.LIVE_MATCH_UPDATE: {
            const {fixture, status} = action.payload;

            if (!fixture || !state.contestData) {
                return {
                    ...state,
                    contestData: {
                        ...state.contestData,
                        status: status || state.contestData.status
                    }
                };
            }

            const newScore = {team1: fixture.homeScore, team2: fixture.awayScore};

            const {fullList, displayList, newRankData} = recalculateState(
                state.leaderboardFull || [],
                newScore,
                state.userRankData,
                state.contestData
            );

            const adjustedUserScore = (state.contestData.type === 'RUGBY')
                ? state.userScore
                : {
                    team1: Math.max(state.userScore.team1, newScore.team1),
                    team2: Math.max(state.userScore.team2, newScore.team2)
                };

            return {
                ...state,
                matchScore: newScore,
                userScore: adjustedUserScore,
                contestData: {
                    ...state.contestData,
                    status: status,
                    fixture: {...state.contestData.fixture, ...fixture}
                },
                leaderboard: displayList,
                leaderboardFull: fullList,
                userRankData: newRankData
            };
        }

        case CONTEST_ACTIONS.LIVE_PARTICIPANT_JOINED: {
            const newPlayer = action.payload;

            const updatedFullList = upsertParticipant(state.leaderboardFull || [], newPlayer);
            const {fullList, displayList, newRankData} = recalculateState(
                updatedFullList,
                state.matchScore,
                state.userRankData,
                state.contestData
            );

            return {
                ...state,
                leaderboard: displayList,
                leaderboardFull: fullList,
                userRankData: newRankData
            };
        }

        case CONTEST_ACTIONS.UPDATE_LEADERBOARD: {
            const {fullList, displayList, newRankData} = recalculateState(
                action.payload.list,
                state.matchScore,
                state.userRankData,
                state.contestData
            );
            return {
                ...state,
                leaderboard: displayList,
                leaderboardFull: fullList,
                userRankData: newRankData
            };
        }

        case CONTEST_ACTIONS.SET_GOAL_OVERLAY:
            if (state.contestData.type === 'FOOTBALL') {
                return {
                    ...state, overlays: {...state.overlays, goal: action.payload}
                };
            } else {
                return state;
            }
        case CONTEST_ACTIONS.SET_QR_OVERLAY:
            return {...state, overlays: {...state.overlays, qr: action.payload}};
        case CONTEST_ACTIONS.SET_SELECTED_PLAYER:
            return {...state, overlays: {...state.overlays, selectedPlayer: action.payload}};

        default:
            return state;
    }
}